import { useContext } from 'react';
import { css } from '@emotion/react';
import { Feature } from '@weave/schema-gen-ts/dist/shared/feature/location_feature.pb';
import { CallRouteV1 } from '@frontend/api-call-route';
import { useTranslation } from '@frontend/i18n';
import { useScopedAppFlagStore } from '@frontend/scope';
import { theme } from '@frontend/theme';
import { RadioCardField, Text, useFormField } from '@frontend/design-system';
import { StepOptions } from '../../helpers/add-step-helpers';
import { Step } from './atoms/step';
import { StepContext } from './provider';

export const SelectStep = ({
  onProceedClick,
  initialStep,
  stepOptions,
}: {
  initialStep?: string;
  stepOptions: StepOptions;
  onProceedClick: (type: CallRouteV1.Types.Node['type']) => void;
}) => {
  const { t } = useTranslation('phone');
  const { getCustomizationFlagValue } = useScopedAppFlagStore();
  const hasVoiceAiEnabled = getCustomizationFlagValue(Feature.TRUELARK_AI_VOICE);

  const { isEditMode } = useContext(StepContext);

  const cardFieldProps = useFormField({
    type: 'radio',
    required: true,
    value: initialStep,
  });

  return (
    <Step
      title={isEditMode ? t('Change Step') : t('Add Step')}
      description={isEditMode ? t('Change this step in the call flow.') : t('Add the next step to the call flow.')}
      primaryButtonLabel={t('Continue')}
      primaryButtonDisabled={!cardFieldProps.value}
      onPrimaryButtonClick={() => {
        const value = cardFieldProps.value;
        onProceedClick(value as CallRouteV1.Types.Node['type']);
      }}
    >
      {stepOptions.hoursAndAvailability.length > 0 && (
        <>
          <Text weight='bold'>{t('Hours and Availability')}</Text>
          <RadioCardField {...cardFieldProps} name='field1' css={cardFieldStyles}>
            {stepOptions.hoursAndAvailability.map((option) => (
              <RadioCardField.Option
                key={option.type}
                css={cardStyles}
                value={option.type}
                title={option.title}
                description={option.description}
                iconName={option.iconName}
              />
            ))}
            <span></span>
          </RadioCardField>
        </>
      )}

      {hasVoiceAiEnabled && stepOptions.weaveAssistant.length > 0 && (
        <>
          <div
            css={{
              display: 'flex',
              alignItems: 'center',
              gap: theme.spacing(1),
            }}
          >
            <Text weight='bold'>{t('AI Receptionist')}</Text>
          </div>
          <RadioCardField {...cardFieldProps} name='field1' css={cardFieldStyles}>
            {stepOptions.weaveAssistant.map((option) => (
              <RadioCardField.Option
                key={option.type}
                css={cardStyles}
                value={option.type}
                title={option.title}
                description={option.description}
                iconName={option.iconName}
              />
            ))}
          </RadioCardField>
        </>
      )}

      <Text weight='bold'>{t('Direct Routing')}</Text>
      <RadioCardField {...cardFieldProps} name='field1' css={cardFieldStyles}>
        {stepOptions.directRouting.map((option) => (
          <RadioCardField.Option
            key={option.type}
            css={cardStyles}
            value={option.type}
            title={option.title}
            description={option.description}
            iconName={option.iconName}
          />
        ))}
      </RadioCardField>

      <Text weight='bold'>{t('Messaging and Information')}</Text>
      <RadioCardField {...cardFieldProps} name='field1' css={cardFieldStyles}>
        {stepOptions.messagingAndInformation.map((option) => (
          <RadioCardField.Option
            key={option.type}
            css={cardStyles}
            value={option.type}
            title={option.title}
            description={option.description}
            iconName={option.iconName}
          />
        ))}
      </RadioCardField>
    </Step>
  );
};
const cardStyles = css`
  flex: unset;
  width: 300px;
`;
const cardFieldStyles = css`
  div:first-child {
    display: flex;
    flex-wrap: wrap;
  }
`;
